<script lang="ts">
	// A3: who signed up, and where each one stopped (UC-21). A restaurant's row
	// opens its account (A4, UC-23). Newest sign-up
	// first, always, with no sort control: sorting by last order or by tills
	// would rank restaurants. Last order is shown as a date and nothing else,
	// because a shop closed for a holiday looks the same as one that stopped.
	// Active until is the subscription's last day (UC-24), with the server's
	// Ending or Ended beneath it; the list never sorts by it either.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { api } from '../api/client';
	import type { components } from '../api/types';
	import { untrack } from 'svelte';
	import { copy } from './copy';
	import { day, instantDay } from './format';
	import { lastChangedAccount, openAccount } from './nav.svelte';
	import { sessionEnded } from './session.svelte';

	type Account = components['schemas']['AdminAccountRowBody'];

	// Long enough that typing an email does not ask the server at every key.
	const SEARCH_PAUSE_MS = 250;

	let query = $state('');
	let rows = $state<Account[]>([]);
	let next = $state('');
	let loaded = $state(false);
	let failed = $state(false);
	let more = $state(false);
	// A More that failed: the rows already shown stay, with a Retry under them.
	let moreFailed = $state(false);
	// The search the rows on screen answer. More continues this one, not
	// text still waiting out its pause.
	let shownQuery = '';
	// The last answer to an empty search had nobody: there is nobody at all.
	let nobody = $state(false);

	let asked = 0;

	async function load(q: string, before = '') {
		const ticket = ++asked;
		try {
			const { data, response } = await api.GET('/api/admin/accounts', {
				params: { query: { ...(q ? { q } : {}), ...(before ? { before } : {}) } },
			});
			if (ticket !== asked) return;
			if (response.status === 401) {
				sessionEnded();
				return;
			}
			if (!data) {
				if (before) moreFailed = true;
				else failed = true;
				return;
			}
			failed = false;
			moreFailed = false;
			rows = before ? [...rows, ...(data.rows ?? [])] : (data.rows ?? []);
			next = data.next;
			shownQuery = q;
			if (!q && !before) nobody = rows.length === 0;
		} catch {
			if (ticket !== asked) return;
			if (before) moreFailed = true;
			else failed = true;
		} finally {
			if (ticket === asked) {
				loaded = true;
				more = false;
			}
		}
	}

	$effect(() => {
		const q = query.trim();
		// The first read goes at once; only typing waits for the pause.
		const pause = untrack(() => loaded) ? SEARCH_PAUSE_MS : 0;
		const timer = setTimeout(() => void load(q), pause);
		return () => clearTimeout(timer);
	});

	// An account changed on its own page (A4): show it as it now is.
	$effect(() => {
		const changed = lastChangedAccount();
		if (!changed?.accountId) return;
		untrack(() => {
			rows = rows.map((r) => (r.accountId === changed.accountId ? changed : r));
		});
	});

	function loadMore() {
		more = true;
		moreFailed = false;
		void load(shownQuery, next);
	}

	const columns = [
		{ label: copy.restaurantOwnerColumn, w: 'minmax(220px,2.2fr)' },
		{ label: copy.signedUpColumn, w: 'minmax(72px,.8fr)' },
		{ label: copy.stateColumn, w: 'minmax(132px,1.2fr)' },
		{ label: copy.shopsColumn, w: 'minmax(48px,.5fr)', align: 'end' as const },
		{ label: copy.tillsColumn, w: 'minmax(48px,.5fr)', align: 'end' as const },
		{ label: copy.lastOrderColumn, w: 'minmax(108px,1fr)' },
		{ label: copy.activeUntilColumn, w: 'minmax(108px,1fr)' },
		{ label: '', w: '64px' },
	];
</script>

<div class="accounts" data-testid="admin-accounts">
	{#if failed && !loaded}
		<Band
			tone="flag"
			action={copy.retry}
			onAction={() => void load(query.trim())}
			testId="admin-accounts-failed"
		>
			{copy.accountsFailed}
		</Band>
	{:else if !loaded}
		<div class="wait">
			<Loading label={copy.accountsLoading} caption={copy.accountsLoading} size="lg" />
		</div>
	{:else if nobody && !query.trim()}
		<Card pad testId="admin-nobody">
			<p class="empty">{copy.nobodyYet}</p>
		</Card>
	{:else}
		<div class="search">
			<Field
				testId="admin-search"
				label={copy.search}
				value={query}
				maxLength={254}
				onInput={(value) => (query = value)}
			/>
		</div>
		{#if failed}
			<Band
				tone="flag"
				action={copy.retry}
				onAction={() => void load(query.trim())}
				testId="admin-accounts-failed"
			>
				{copy.accountsFailed}
			</Band>
		{:else if rows.length === 0}
			<Card pad testId="admin-no-match">
				<p class="empty">{copy.noMatch}</p>
			</Card>
		{:else}
			<TablePanel
				{columns}
				title={copy.accounts}
				note={query.trim() ? copy.matchesNote : copy.accountsNote}
				rowMin="var(--row-min-wide)"
				testId="admin-accounts-list"
			>
				{#each rows as r (r.ownerEmail + r.signedUpAt)}
					<Row testId="admin-account-{r.ownerEmail}">
						<span class="who">
							{#if r.accountId}
								<span class="restaurant">{r.restaurantName}</span>
							{:else}
								<span class="none">{copy.noRestaurant}</span>
							{/if}
							<span class="owner">{r.ownerName} · {r.ownerEmail}</span>
						</span>
						<span class="mono small">{instantDay(r.signedUpAt)}</span>
						<span class="state">
							{#if r.state === 'suspended' && r.suspendedAt}
								<Chip text={copy.suspended(instantDay(r.suspendedAt))} tone="flag" size="md" dot />
							{:else if r.accountId}
								<Chip text={copy.active} tone="ok" size="md" dot />
							{/if}
						</span>
						<span class="mono end">{r.accountId ? r.shopCount : ''}</span>
						<span class="mono end">{r.accountId ? r.tillCount : ''}</span>
						<span class="small body">
							{#if r.accountId}{r.lastOrderDate ? day(r.lastOrderDate) : copy.noOrders}{/if}
						</span>
						<span class="until">
							{#if r.accountId}
								{#if r.subscriptionEndsOn}
									<span class="mono small strong">{day(r.subscriptionEndsOn)}</span>
								{:else}
									<span class="small body">{copy.noEndDate}</span>
								{/if}
								{#if r.subscriptionNotice === 'ending' || r.subscriptionNotice === 'ended'}
									<Chip
										text={r.subscriptionNotice === 'ended' ? copy.ended : copy.ending}
										tone="flag"
										size="sm"
										dot
										testId="admin-account-notice-{r.ownerEmail}"
									/>
								{/if}
							{/if}
						</span>
						<span class="open">
							{#if r.accountId}
								<Button
									testId="admin-account-open-{r.ownerEmail}"
									label={copy.open}
									tone="text"
									size="sm"
									onPress={() => openAccount(r.accountId)}
								/>
							{/if}
						</span>
					</Row>
				{/each}
			</TablePanel>
			{#if moreFailed}
				<Band
					tone="flag"
					action={copy.retry}
					onAction={loadMore}
					testId="admin-accounts-more-failed"
				>
					{copy.accountsFailed}
				</Band>
			{:else if next}
				<div class="more">
					<Button
						testId="admin-accounts-more"
						label={copy.more}
						tone="secondary"
						size="md"
						state={more ? 'pending' : 'idle'}
						pendingLabel={copy.moreLoading}
						onPress={loadMore}
					/>
				</div>
			{/if}
			<p class="note">{copy.setupNote}</p>
		{/if}
	{/if}
</div>

<style>
	.accounts {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.empty {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.search {
		max-inline-size: 420px;
	}

	.who {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.restaurant {
		font-weight: 600;
		color: var(--text-strong);
	}

	.none {
		font-weight: 600;
		color: var(--text-muted);
	}

	.owner {
		font-size: var(--text-2xs);
		color: var(--text-body);
		overflow-wrap: anywhere;
	}

	.mono {
		min-inline-size: 0;
		font-family: var(--font-mono);
	}

	.small {
		font-size: var(--text-sm);
	}

	.end {
		text-align: end;
	}

	.state {
		min-inline-size: 0;
		display: flex;
	}

	.body {
		min-inline-size: 0;
		color: var(--text-body);
	}

	.more {
		display: flex;
	}

	.until {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-1);
	}

	.strong {
		color: var(--text-strong);
	}

	.open {
		min-inline-size: 0;
		display: flex;
		justify-content: flex-end;
	}

	.note {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}
</style>
